import { useEffect, useRef } from 'react';
import { X } from '@ui/shared/icons';

/**
 * Minimal bottom-sheet shell for a plain list container (today-phone-
 * parity.md §4.5's "+N more" overflow — Priorities' 4th+ row). No existing
 * `ConfirmSheet`-class primitive exists in this codebase to restyle (the
 * spec names `ConfirmSheet` as the nearest reference but that component
 * doesn't exist yet either), so this is the small, minimal addition the
 * spec explicitly pre-authorizes: NOT a full-screen route/navigation (fixed
 * to the viewport bottom, dismissible, the same "reveal within the row, not
 * a popover" register as everything else on this page), just enough chrome
 * to host a plain list with a header and a close affordance.
 */
export function ListSheet({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'color-mix(in srgb, black 40%, transparent)' }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'fixed',
          zIndex: 51,
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: '70vh',
          overflowY: 'auto',
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-4)',
          paddingBottom: 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            style={{
              width: 'var(--touch-target-min)',
              height: 'var(--touch-target-min)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div style={{ marginTop: 'var(--space-2)' }}>{children}</div>
      </div>
    </>
  );
}
